import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

const Dashboard = lazy(() => import('../../views/Dashboard/Dashboard'));
const Permissions = lazy(() => import('../../views/Permissions/Permissions'));
const Advertisers = lazy(() => import('../../views/Advertisers/Advertisers'));
const MarketingPartnerPage = lazy(() => import('../../views/MarketingPartners/MarketingPartners'));
const Domain = lazy(() => import('../../views/Domains/Domain'));
const RevSharePercentage = lazy(() => import('../../views/RevSharePercentage/RevSharePercentage'));
const PublisherPrePings = lazy(() => import('../../views/PublisherPrePings/PublisherPrePings'));
const PublisherPrePingsDetails = lazy(
  () => import('../../views/PublisherPrePings/PublisherPrePingsDetails/PublisherPrePingDetails')
);
const PubxmlFeeds = lazy(() => import('../../views/PubxmlFeeds/PubxmlFeeds'));
const PubxmlFeedDetails = lazy(() => import('../../views/PubxmlFeeds/PubxmlFeedDetails/PubxmlFeedDetails'));
const SourceFeeds = lazy(() => import('../../views/SourceFeeds/SourceFeeds'));
const VirtualSourceFeeds = lazy(() => import('../../views/VirtualSourceFeeds/VirtualSourceFeeds'));
const FieldMappings = lazy(() => import('../../views/FieldMappings/FieldMappings'));
const VirtualSourceFeedDetails = lazy(
  () => import('../../views/VirtualSourceFeeds/VirtualSourceFeedDetails/VirtualSourceFeedDetails')
);
const SourceFeedDetails = lazy(() => import('../../views/SourceFeeds/SourceFeedDetails/SourceFeedDetails'));
const GenericJobsSummary = lazy(() => import('../../views/GenericJobs/GenericJobsSummary'));
const GenericJobDetail = lazy(() => import('../../views/GenericJobs/GenericJobDetail/GenericJobDetail'));
const GenericJobCompanies = lazy(() => import('../../views/GenericJobs/Companies/Companies'));
const GenericJobCategories = lazy(() => import('../../views/GenericJobs/JobCategories/JobCategories'));
const LandingPages = lazy(() => import('../../views/LandingPages/LandingPages'));
const LandingPageDetails = lazy(() => import('../../views/LandingPages/LandingPageDetails/LandingPageDetails'));
const LandingPageGroups = lazy(() => import('../../views/LandingPageGroups/LandingPageGroups'));
const LandingPageGroupDetails = lazy(() => import('../../views/LandingPageGroups/LPGDetails/LPGDetails'));
const OfferTable = lazy(() => import('../../views/Offers/OfferTable'));
const OfferDetails = lazy(() => import('../../views/Offers/OfferDetails'));
const OfferActions = lazy(() => import('../../views/Offers/OfferActions/OfferActions'));
const OfferActionDetail = lazy(() => import('../../views/Offers/OfferActions/OfferActionDetail'));
const OfferWallAds = lazy(() => import('../../views/OfferWallAds/OfferWallAds'));
const OfferWallAdDetails = lazy(() => import('../../views/OfferWallAds/OfferWallAdDetails/OfferWallAdDetails'));
const AdvertiserPrePing = lazy(() => import('../../views/AdvPrePing/AdvPrePing'));
const AdvertiserPrePingDetails = lazy(() => import('../../views/AdvPrePing/AdvPrePingDetails/AdvPrePingDetails'));
const Users = lazy(() => import('../../views/Users/Users'));
const UserDetails = lazy(() => import('../../views/Users/UserDetails'));
const Sequences = lazy(() => import('../../views/Sequences/Sequences'));
const Tags = lazy(() => import('../../views/Tags/Tags'));
const LayoutsList = lazy(() => import('../../views/Layouts/LayoutsList'));
const LayoutsDetails = lazy(() => import('../../views/Layouts/LayoutsDetails'));
const LayoutsDetailsSPA = lazy(() => import('../../views/Layouts/LayoutsDetailsSPA'));
const TemplateEditor = lazy(() => import('../../views/TemplateEditor/TemplateEditor'));
const TestLinkBuilder = lazy(() => import('../../views/TestLinkBuilder/TestLinkBuilder'));
const ContainersList = lazy(() => import('../../views/Containers/ContainersList'));
const ContainersDetails = lazy(() => import('../../views/Containers/ContainersDetails'));
const OfferBlocksSummary = lazy(() => import('../../views/OfferBlocks/OfferBlocksSummary'));
const HandlersSummary = lazy(() => import('../../views/Handlers/HandlersSummary'));
const UserAttribute = lazy(() => import('../../views/UserAttributes/UserAttributeTable'));
const ZipCodes = lazy(() => import('../../views/ZipCodes/ZipCodes'));
const FeatureToggles = lazy(() => import('../../views/FeatureToggles/FeatureToggles'));
const Properties = lazy(() => import('../../views/CustomProperties/Properties/Properties'));
const PropertySets = lazy(() => import('../../views/CustomProperties/PropertySets/PropertySets'));
const PropertySetDetails = lazy(() => import('../../views/CustomProperties/PropertySets/details/PropertySetDetails'));
const EntityGroups = lazy(() => import('../../views/CustomProperties/EntityGroups/EntityGroups'));
const EntityGroupDetails = lazy(() => import('../../views/CustomProperties/EntityGroups/details/EntityGroupDetails'));
const GlobalProperties = lazy(() => import('../../views/CustomProperties/GlobalProperties/GlobalProperties'));
const SiteScreen = lazy(() => import('../../views/Sites/SiteScreen'));
const SiteDetail = lazy(() => import('../../views/Sites/SiteDetail/SiteDetail'));
const AffiliatesSummary = lazy(() => import('../../views/Affiliates/AffiliatesSummary'));
const DynamicOfferSections = lazy(() => import('../../views/DynamicOfferSections/DynamicOfferSections'));
const MediaLibrarySummary = lazy(() => import('../../views/MediaLibrary/MediaLibrarySummary'));
const Page404 = lazy(() => import('../../views/Pages/Page404/Page404'));

/** Authenticated application routes, rendered inside DefaultLayout's content area. */
export default function AppRoutes() {
  return (
    <Routes>
      <Route path='/' element={<Navigate to='/dashboard' replace />} />
      <Route path='/dashboard' element={<Dashboard />} />
      <Route path='/advertisers' element={<Advertisers />} />
      <Route path='/permissions' element={<Permissions />} />
      <Route path='/marketingpartners' element={<MarketingPartnerPage />} />
      <Route path='/domains' element={<Domain />} />
      <Route path='/affiliates' element={<AffiliatesSummary />} />
      <Route path='/publisher-pre-pings' element={<PublisherPrePings />} />
      <Route path='/publisher-pre-pings/create' element={<PublisherPrePingsDetails />} />
      <Route path='/publisher-pre-pings/:publisherPrePingId' element={<PublisherPrePingsDetails />} />
      <Route path='/pubxml-feeds' element={<PubxmlFeeds />} />
      <Route path='/pubxml-feeds/create' element={<PubxmlFeedDetails />} />
      <Route path='/pubxml-feeds/:pubxmlFeedId' element={<PubxmlFeedDetails />} />
      <Route path='/source-feeds' element={<SourceFeeds />} />
      <Route path='/field-mappings' element={<FieldMappings />} />
      <Route path='/virtual-source-feeds' element={<VirtualSourceFeeds />} />
      <Route path='/virtual-source-feeds/create' element={<VirtualSourceFeedDetails />} />
      <Route path='/virtual-source-feeds/:vsfId' element={<VirtualSourceFeedDetails />} />
      <Route path='/source-feeds/create' element={<SourceFeedDetails />} />
      <Route path='/source-feeds/:sourceFeedId' element={<SourceFeedDetails />} />
      <Route path='/advertiser-pre-pings' element={<AdvertiserPrePing />} />
      <Route path='/advertiser-pre-pings/create' element={<AdvertiserPrePingDetails />} />
      <Route path='/advertiser-pre-pings/:advPrePingId' element={<AdvertiserPrePingDetails />} />
      <Route path='/revshare-percentage' element={<RevSharePercentage />} />
      <Route path='/campaigns/landingpages' element={<LandingPages />} />
      <Route path='/campaigns/landingpages/landingpage-detail' element={<LandingPageDetails />} />
      <Route path='/campaigns/landingpages/:landingPageId' element={<LandingPageDetails />} />
      <Route path='/campaigns/landingpagegroups' element={<LandingPageGroups />} />
      <Route path='/campaigns/landingpagegroups/create' element={<LandingPageGroupDetails />} />
      <Route path='/campaigns/landingpagegroups/:lpgId' element={<LandingPageGroupDetails />} />
      <Route path='/campaigns/offerblocks' element={<OfferBlocksSummary />} />
      <Route path='/campaigns/offertable/offer/:offerId' element={<OfferDetails />} />
      <Route path='/campaigns/offertable/offer/:offerId?selectedOffer=:selectedOffer' element={<OfferDetails />} />
      <Route path='/campaigns/offertable/offer-detail' element={<OfferDetails />} />
      <Route path='/campaigns/offertable' element={<OfferTable />} />
      <Route path='/campaigns/offerwallads/create' element={<OfferWallAdDetails />} />
      <Route path='/campaigns/offerwallads/:offerWallAdId' element={<OfferWallAdDetails />} />
      <Route path='/campaigns/offerwallads' element={<OfferWallAds />} />
      <Route path='/campaigns' element={<OfferTable />} />
      <Route path='/campaigns/dynamic-offer-sections' element={<DynamicOfferSections />} />
      <Route path='/campaigns/offeractions' element={<OfferActions />} />
      <Route path='/campaigns/offeractions/offer-action-detail' element={<OfferActionDetail />} />
      <Route path='/campaigns/offeractions/:actionId' element={<OfferActionDetail />} />
      <Route path='/users' element={<Users />} />
      <Route path='/userdetails/:userId' element={<UserDetails />} />
      <Route path='/campaigns/sequences' element={<Sequences />} />
      <Route path='/campaigns/tags' element={<Tags />} />
      <Route path='/content/layouts' element={<LayoutsList />} />
      <Route path='/content/spa/layouts/:layoutId' element={<LayoutsDetailsSPA />} />
      <Route path='/content/spa/layouts/create' element={<LayoutsDetailsSPA />} />
      <Route path='/content/layouts/:layoutId' element={<LayoutsDetails />} />
      <Route path='/content/layouts/create' element={<LayoutsDetails />} />
      <Route path='/content/containers' element={<ContainersList />} />
      <Route path='/content/containers/:containerId' element={<ContainersDetails />} />
      <Route path='/content/containers/create' element={<ContainersDetails />} />
      <Route path='/media-library' element={<MediaLibrarySummary />} />
      <Route path='/features' element={<FeatureToggles />} />
      <Route path='/content/handlers' element={<HandlersSummary />} />
      <Route path='/content/template-editor' element={<TemplateEditor />} />
      <Route path='/tools/test-link-builder' element={<TestLinkBuilder />} />
      <Route path='/content/userAttributes' element={<UserAttribute />} />
      <Route path='/content/zipCodes' element={<ZipCodes />} />
      <Route path='/custom-properties/properties' element={<Properties />} />
      <Route path='/custom-properties/property-sets' element={<PropertySets />} />
      <Route path='/custom-properties/property-sets/:propertySetId' element={<PropertySetDetails />} />
      <Route path='/custom-properties/property-sets/create' element={<PropertySetDetails />} />
      <Route path='/custom-properties/entity-groups' element={<EntityGroups />} />
      <Route path='/custom-properties/entity-groups/:entityGroupId' element={<EntityGroupDetails />} />
      <Route path='/custom-properties/global-properties' element={<GlobalProperties />} />
      <Route path='/campaigns/sites' element={<SiteScreen />} />
      <Route path='/campaigns/sites/:siteId' element={<SiteDetail />} />
      <Route path='/generic-jobs' element={<GenericJobsSummary />} />
      <Route path='/generic-jobs/companies' element={<GenericJobCompanies />} />
      <Route path='/generic-jobs/job-categories' element={<GenericJobCategories />} />
      <Route path='/generic-jobs/:jobId' element={<GenericJobDetail />} />
      {/* Keep last: anything the list above did not match. */}
      <Route path='*' element={<Page404 />} />
    </Routes>
  );
}
